import React, { useState, useEffect, useRef } from 'react';
import { getChatSessions, getChatMessages, replyToChat, markSessionResolved } from '../../controllers/chatbot.controller';
import socket, { initSocket } from '../../utils/socket';
import toast from 'react-hot-toast';
import { IconSend, IconCheck, IconUser, IconClock, IconMessage } from '@tabler/icons-react';

export default function ChatInboxPage() {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchSessions();
    
    // Listen for new messages globally to update session list if needed
    const handleAdminChatUpdate = () => {
      fetchSessions();
      if (activeSession) {
        fetchMessages(activeSession.id);
      }
    };
    
    // Ensure socket is connected
    initSocket();

    // SuperAdmin should join a global room for admin notifications
    socket.emit("join_admin_room");
    socket.on("admin_new_chat", handleAdminChatUpdate);
    
    return () => {
      socket.off("admin_new_chat", handleAdminChatUpdate);
    };
  }, [activeSession]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchSessions = async () => {
    try {
      const data = await getChatSessions();
      setSessions(Array.isArray(data) ? data : (data.sessions || []));
      setIsLoading(false);
    } catch (error) {
      toast.error('Failed to load chat sessions');
      setIsLoading(false);
    }
  };

  const fetchMessages = async (sessionId) => {
    try {
      const data = await getChatMessages(sessionId);
      setMessages(Array.isArray(data) ? data : (data.messages || []));
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const handleSelectSession = (session) => {
    setActiveSession(session);
    fetchMessages(session.id);
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !activeSession) return;
    
    const text = replyText;
    setReplyText('');
    
    // Optimistic UI
    setMessages(prev => [...prev, {
      id: Date.now(),
      sender_type: 'admin',
      message_text: text,
      created_at: new Date().toISOString()
    }]);

    try {
      await replyToChat(activeSession.id, text);
      // Actual fetch handled by socket or we can refetch here just in case
    } catch (error) {
      toast.error('Failed to send message');
      fetchMessages(activeSession.id); // revert optimistic UI
    }
  };

  const handleResolve = async () => {
    if (!activeSession) return;
    try {
      await markSessionResolved(activeSession.id);
      toast.success('Session marked as resolved');
      setActiveSession(null);
      fetchSessions();
    } catch (error) {
      toast.error('Failed to resolve session');
    }
  };

  return (
    <div className="p-6 h-[calc(100vh-80px)] flex flex-col">
      <div className="mb-6 shrink-0">
        <h1 className="text-3xl font-bold text-gray-900">Support Inbox</h1>
        <p className="text-gray-500 mt-1">Manage unresolved visitor queries and chat in real-time.</p>
      </div>

      <div className="flex-1 flex bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden min-h-0">
        
        {/* Sidebar: Session List */}
        <div className="w-1/3 border-r border-gray-200 flex flex-col bg-gray-50">
          <div className="p-4 border-b border-gray-200 bg-white">
            <h2 className="font-bold text-gray-700 flex items-center gap-2">
              <IconMessage size={20}/> Active Conversations
            </h2>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="p-8 text-center"><span className="loading loading-spinner text-primary"></span></div>
            ) : sessions.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">No active conversations.</div>
            ) : (
              sessions.map(s => (
                <div 
                  key={s.id} 
                  onClick={() => handleSelectSession(s)}
                  className={`p-4 border-b border-gray-100 cursor-pointer transition-colors ${activeSession?.id === s.id ? 'bg-blue-50 border-blue-100' : 'hover:bg-white'}`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-bold text-gray-800 flex items-center gap-1">
                      <IconUser size={16}/> {s.visitor_name || 'Visitor'}
                    </span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase font-bold tracking-wider ${s.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                      {s.status}
                    </span>
                  </div>
                  <div className="flex items-center text-xs text-gray-400 gap-1 mt-2">
                    <IconClock size={14} /> {new Date(s.updated_at).toLocaleString()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="w-2/3 flex flex-col bg-white relative">
          {activeSession ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-white shadow-sm z-10 shrink-0">
                <div>
                  <h3 className="font-bold text-lg text-gray-800">{activeSession.visitor_name || 'Visitor'}</h3>
                  <p className="text-xs text-gray-500">Session ID: {activeSession.id} {activeSession.visitor_phone ? `| Phone: ${activeSession.visitor_phone}` : ''}</p>
                </div>
                {activeSession.status === 'active' && (
                  <button onClick={handleResolve} className="btn btn-sm btn-outline btn-success">
                    <IconCheck size={16} /> Mark Resolved
                  </button>
                )}
              </div>

              {/* Messages Area */}
              <div className="flex-1 p-6 overflow-y-auto bg-slate-50 space-y-6">
                {messages.map(msg => {
                  const isVisitor = msg.sender_type === 'user';
                  return (
                    <div key={msg.id} className={`flex ${isVisitor ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[75%] rounded-2xl p-4 shadow-sm ${isVisitor ? 'bg-white text-gray-800 rounded-tl-none border border-gray-100' : 'bg-blue-600 text-white rounded-tr-none'}`}>
                        <div className="text-xs opacity-70 mb-1 font-medium capitalize">
                          {msg.sender_type}
                        </div>
                        <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.message_text}</p>
                        {msg.answer_image && (
                          <img src={msg.answer_image.startsWith('/') ? msg.answer_image : `/${msg.answer_image}`} alt="Attachment" className="mt-3 rounded-lg w-full max-w-sm border border-gray-200" />
                        )}
                        <div className={`text-[10px] mt-2 text-right ${isVisitor ? 'text-gray-400' : 'text-blue-200'}`}>
                          {new Date(msg.created_at).toLocaleTimeString()}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="p-4 border-t border-gray-200 bg-white shrink-0">
                <form onSubmit={handleSendReply} className="flex gap-3">
                  <input
                    type="text"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    disabled={activeSession.status !== 'active'}
                    placeholder={activeSession.status === 'active' ? "Type a reply to the visitor..." : "Session is resolved. Cannot reply."}
                    className="flex-1 input input-bordered rounded-full bg-gray-50"
                  />
                  <button 
                    type="submit" 
                    disabled={!replyText.trim() || activeSession.status !== 'active'}
                    className="btn btn-primary btn-circle shadow-md disabled:bg-gray-200"
                  >
                    <IconSend size={20} />
                  </button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
              <IconMessage size={64} className="mb-4 opacity-20" />
              <p className="text-lg">Select a conversation to start chatting</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
